import React from 'react';
import { FeatureFlagsParsed } from '@/contexts/Flags/featureFlags.types';
import { EnRoles } from '@/interfaces/Roles';
import {
    UserOutlined,
    ShopOutlined,
    TagsOutlined,
    BellOutlined,
    AppstoreOutlined,
    WalletOutlined,
    FileTextOutlined,
    DashboardOutlined,
    NotificationOutlined,
    ShoppingOutlined,
    DatabaseOutlined,
    SettingOutlined,
} from '@ant-design/icons';
import { MenuItemType } from 'rc-menu/lib/interface';

// Roles que compõem o "admin" (equivalente ao antigo WithAdmin + isAdmin)
const adminRoles = [EnRoles.sysAdmin, EnRoles.admin, EnRoles.roAdmin];

export const rawMenu = (
    flags: FeatureFlagsParsed,
    conditions: {
        canChangeTaxData: boolean;
        emitInvoice: boolean;
        hasChurnAccess: boolean;
    },
): MenuItemType[] => {
    return [
        {
            key: '/',
            label: 'Dashboard',
            path: '/dashboard',
            icon: <DashboardOutlined />,
            roles: [EnRoles.shopManager, ...adminRoles],
        },
        {
            key: '/users',
            label: 'Usuários',
            icon: <UserOutlined />,
            roles: adminRoles,
            children: [
                {
                    key: '/users/admin/list',
                    label: 'Administradores',
                    path: '/users/admin/list',
                    roles: adminRoles,
                },
                {
                    key: '/users/shopmanager/list',
                    label: 'Lojistas',
                    path: '/users/shopmanager/list',
                    roles: adminRoles,
                },
                {
                    key: '/users/client/list',
                    label: 'Clientes',
                    path: '/users/client/list',
                    roles: adminRoles,
                },
            ],
        },
        {
            key: '/manage',
            label: 'Gerenciar',
            icon: <AppstoreOutlined />,
            roles: [EnRoles.shopManager],
            children: [
                {
                    key: '/stores/list',
                    label: 'Lojas',
                    path: '/stores/list',
                    roles: [
                        EnRoles.shopManager,
                        EnRoles.sysAdmin,
                        EnRoles.admin,
                    ],
                },
                {
                    key: '/products/list',
                    label: 'Produtos',
                    path: '/products/list',
                    roles: [
                        EnRoles.shopManager,
                        EnRoles.sysAdmin,
                        EnRoles.admin,
                    ],
                },
                {
                    key: flags?.PUSH_NOTIFICATION_V2
                        ? '/notifications/v2/list'
                        : '/notifications/list',
                    label: 'Notificações PUSH',
                    path: flags?.PUSH_NOTIFICATION_V2
                        ? '/notifications/v2/list'
                        : '/notifications/list',
                    roles: [EnRoles.shopManager, EnRoles.sysAdmin],
                },
                {
                    key: '/adverts/list',
                    label: 'Anúncios',
                    path: '/adverts/list',
                    roles: [EnRoles.admin, EnRoles.shopManager],
                },
                {
                    key: '/transfer/list',
                    label: 'Transferências',
                    path: '/transfer/list',
                    roles: [
                        EnRoles.shopManager,
                        EnRoles.sysAdmin,
                        EnRoles.admin,
                    ],
                },
                {
                    key: '/wallet/balance',
                    label: 'Saldo em Carteira',
                    path: '/wallet/balance',
                    roles: [
                        EnRoles.shopManager,
                        EnRoles.sysAdmin,
                        EnRoles.admin,
                    ],
                },
                {
                    key: '/coupons/list',
                    label: 'Cupons',
                    path: '/coupons/list',
                    roles: [
                        EnRoles.shopManager,
                        EnRoles.sysAdmin,
                        EnRoles.admin,
                    ],
                },
                {
                    key: '/planogram/automation',
                    label: 'Planogramas',
                    path: '/planogram/automation',
                    roles: [EnRoles.shopManager],
                    flag: 'AUTOMATION_PLANOGRAM',
                },
                {
                    key: '/shopmanagers/users/list',
                    label: 'Usuários',
                    path: '/shopmanagers/users/list',
                    roles: [EnRoles.shopManager],
                },
                {
                    key: '/invoice',
                    label: 'Boletos e Cobranças',
                    path: '/invoice',
                    roles: [EnRoles.shopManager],
                },
                ...(conditions.emitInvoice
                    ? [
                          {
                              key: '/nfe',
                              label: 'Notas Fiscais',
                              path: '/nfe',
                              roles: [EnRoles.shopManager],
                          },
                      ]
                    : []),
            ],
        },
        {
            key: '/reports',
            label: 'Relatórios',
            icon: <FileTextOutlined />,
            roles: [EnRoles.shopManager],
            children: [
                {
                    key: '/stocks/list',
                    path: '/stocks/list',
                    label: 'Estoques',
                    roles: [EnRoles.shopManager],
                },
                {
                    key: '/stocks/adjustments',
                    path: '/stocks/adjustments',
                    label: 'Ajustes de Estoque',
                    roles: [EnRoles.shopManager],
                },
                {
                    key: '/purchase/list',
                    path: '/purchase/list',
                    label: 'Compras',
                    roles: [EnRoles.shopManager],
                },
                {
                    key: '/sales/list',
                    path: '/sales/list',
                    label: 'Vendas',
                    roles: [EnRoles.shopManager],
                },
                {
                    key: '/financial',
                    path: '/financial',
                    label: 'Financeiro',
                    roles: [EnRoles.shopManager],
                },
                {
                    key: '/imports/reports',
                    path: '/imports/reports',
                    label: 'Importações',
                    roles: [EnRoles.shopManager],
                },
                {
                    key: '/products/reports',
                    path: '/products/reports',
                    label: 'Produtos',
                    roles: [EnRoles.shopManager],
                },
            ],
        },
        {
            key: '/stores',
            label: 'Lojas',
            path: '/stores',
            icon: <ShopOutlined />,
            roles: adminRoles,
        },
        {
            key: '/stocks/list',
            label: 'Estoque',
            path: '/stocks/list',
            icon: <DatabaseOutlined />,
            roles: adminRoles,
        },
        {
            key: '/product_global/list',
            label: 'Produtos',
            path: '/product_global/list',
            icon: <ShoppingOutlined />,
            roles: adminRoles,
        },
        {
            key: '/sales/list',
            label: 'Vendas',
            path: '/sales/list',
            icon: <TagsOutlined />,
            roles: adminRoles,
        },
        {
            key: flags?.PUSH_NOTIFICATION_V2
                ? '/notifications/v2/list'
                : '/notifications/list',
            label: 'Notificações PUSH',
            path: flags?.PUSH_NOTIFICATION_V2
                ? '/notifications/v2/list'
                : '/notifications/list',
            roles: adminRoles,
            icon: <BellOutlined />,
        },
        {
            key: '/imports/reports',
            label: 'Importações',
            path: '/imports/reports',
            icon: <FileTextOutlined />,
            roles: adminRoles,
        },
        {
            key: '/notification/logs',
            label: 'Logs de notificação',
            path: '/notification/logs',
            icon: <FileTextOutlined />,
            roles: adminRoles,
        },
        {
            key: '/adverts/admin/list',
            label: 'Anúncios',
            path: '/adverts/admin/list',
            icon: <AppstoreOutlined />,
            roles: adminRoles,
        },
        {
            key: '/announcements',
            label: 'Comunicados',
            path: '/announcements',
            roles: adminRoles,
            icon: <NotificationOutlined />,
        },
        {
            key: '/totems',
            label: 'Gerenciamento de totens',
            path: '/totems',
            icon: <SettingOutlined />,
            roles: adminRoles,
        },
        {
            key: '/announcements/view',
            label: 'Comunicados',
            path: '/announcements/view',
            roles: [EnRoles.shopManager],
            icon: <NotificationOutlined />,
        },
        {
            key: '/invoice',
            label: 'Boletos e Cobranças',
            path: '/invoice',
            icon: <FileTextOutlined />,
            roles: adminRoles,
        },
        conditions.canChangeTaxData && {
            key: '/shopmanager/tax-data',
            label: 'Configurações Fiscais',
            path: '/shopmanager/tax-data',
            icon: <SettingOutlined />,
            roles: [EnRoles.shopManager],
        },
        flags?.CHARGE_LOOSE && {
            key: '/charges',
            label: 'Cobrança Automática Avulsa',
            path: '/charges',
            icon: <WalletOutlined />,
            roles: adminRoles,
        },
        conditions.hasChurnAccess && {
            key: '/churn',
            label: 'Risco de Churn',
            path: '/churn',
            icon: <DatabaseOutlined />,
            roles: adminRoles,
        },
    ].filter(Boolean) as MenuItemType[];
};
